import type React from 'react';

type StatusSkeletonLoadingProps = {
    count?: number
    disabled?: boolean
    isDetailStatus?: boolean
    isFollowPage?: boolean
    onClick?: () => void
}

type StatusSkeletonItemProps = {
    index: number
    count: number
    isFollowPage: boolean
    isDetailStatus: boolean
    isNotificationsPage?: boolean
    notificationType?: 'favorite' | 'follow' | 'reply'
}

const StatusSkeletonItem: React.FC<StatusSkeletonItemProps> = ({ index, count, isFollowPage, isDetailStatus, isNotificationsPage, notificationType }) => {
    const lineHeight = 50 + (index % 3) * 5;

    if (count === 1 && isDetailStatus) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'start', gap: '8px' }} className='comment-wrapper'>
                <div className='status-skeleton-item animate-pulse' style={{ width: '100%', paddingBottom: '50px'}}>
                    <div className='skeleton-content' style={{ borderRadius: '0 !important' }}>
                        <div className='skeleton-avatar' />

                        <div className='skeleton-main'>
                            <div className='skeleton-header'>
                                <div className='skeleton-user-info'>
                                    <div className='skeleton-username' />
                                    {index % 2 === 0 && (
                                        <div className='skeleton-follow-btn' />
                                    )}
                                </div>
                                <div className='skeleton-menu'>
                                    <div className='skeleton-dot' />
                                    <div className='skeleton-dot' />
                                    <div className='skeleton-dot' />
                                </div>
                            </div>

                            <div className='skeleton-timestamp'>
                                <div className='skeleton-clock-icon' />
                                <div className='skeleton-time' />
                            </div>

                            <div className='skeleton-content-text'>
                                {index % 2 === 0 ? (
                                    <>
                                        <div className='skeleton-line three-quarter' />
                                        <div
                                            className='skeleton-line full-width'
                                            style={{
                                                height: `${lineHeight}px`,
                                            }}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <div
                                            className='skeleton-line full-width'
                                            style={{
                                                height: `${lineHeight}px`,
                                            }}
                                        />
                                        <div className='skeleton-line three-quarter' />
                                    </>
                                )}
                            </div>

                            <div className='skeleton-stats'>
                                <div className='skeleton-likes'>
                                    <div className='skeleton-like-count' />
                                </div>
                                <div className='skeleton-engagement'>
                                    <div className='skeleton-stat comment' />
                                    <div className='skeleton-stat share' />
                                    <div className='skeleton-stat views' />
                                </div>
                            </div>

                            <div className='skeleton-actions'>
                                <div className='skeleton-buttons'>
                                    <div className='skeleton-action-btn'>
                                        <div className='skeleton-icon' />
                                        <div className='skeleton-label like' />
                                    </div>
                                    <div className='skeleton-action-btn'>
                                        <div className='skeleton-icon' />
                                        <div className='skeleton-label comment' />
                                    </div>
                                    <div className='skeleton-action-btn'>
                                        <div className='skeleton-icon' />
                                        <div className='skeleton-label share' />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {Array.from({ length: count + 2 }, (_, index) => (
                    <div key={index} className='comment-skeleton-item animate-pulse' style={{ width: '100%' }}>
                        <div className='skeleton-content'>
                            <div className='skeleton-avatar comment-avatar' />
                            <div className='skeleton-main'>
                                <div className='skeleton-header comment-header'>
                                    <div className='skeleton-user-info'>
                                        <div className='skeleton-username comment-username' />
                                        <div className='skeleton-timestamp comment-timestamp'>
                                            <div className='skeleton-clock-icon small' />
                                            <div className='skeleton-time small' />
                                            <div className='skeleton-follow-btn small' />
                                        </div>
                                    </div>
                                </div>

                                <div className='skeleton-content-text comment-content'>
                                    <div className='skeleton-line full-width' style={{ height: `${Math.max(20, lineHeight - 30)}px` }} />
                                    {index % 3 === 0 && <div className='skeleton-line half-width' />}
                                </div>

                                <div className='skeleton-actions comment-actions'>
                                    <div className='skeleton-buttons small-buttons'>
                                        <div className='skeleton-action-btn small'>
                                            <div className='skeleton-icon small' />
                                            <div className='skeleton-count' />
                                        </div>
                                        <div className='skeleton-action-btn small'>
                                            <div className='skeleton-icon small' />
                                            <div className='skeleton-count' />
                                        </div>
                                        <div className='skeleton-menu-dots'>
                                            <div className='skeleton-dot small' />
                                            <div className='skeleton-dot small' />
                                            <div className='skeleton-dot small' />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (isFollowPage) {
        return (
            <div className='follow-skeleton-item animate-pulse'>
                <div className='follow-content'>
                    <div className='skeleton-avatar follow-avatar' />
                    <div className='follow-main'>
                        <div className='follow-user-info'>
                            <div className='skeleton-username follow-username' />
                            <div className='skeleton-follower-count' />
                        </div>
                    </div>
                    <div className='follow-actions'>
                        <div className='skeleton-action-btn follow-btn'>
                            <div className='skeleton-follow-label' />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isNotificationsPage && notificationType === 'favorite') {
        return (
            <div className='notification-skeleton-item favorite-notification animate-pulse status-skeleton-item-notification'>
                <div className='notification-icons'>
                    <div className='skeleton-like-icon' />
                    <div className='skeleton-avatar-small' />
                </div>
                <div className='notification-content'>
                    <div className='notification-main'>
                        <div className='skeleton-username-bold' />
                        <div className='skeleton-action-text' />
                        <div className='skeleton-timestamp' />
                    </div>
                    <div className='notification-target'>
                        <div className='skeleton-admin-badge' />
                        <div className='skeleton-notification-id' />
                    </div>
                </div>
            </div>
        );
    }

    if (isNotificationsPage && notificationType === 'follow') {
        return (
            <div className='notification-skeleton-item follow-notification animate-pulse status-skeleton-item-notification'>
                <div className='notification-icons'>
                    <div className='skeleton-people-icon' />
                    <div className='skeleton-avatar-small' />
                    <div className='skeleton-app-icon' />
                </div>
                <div className='notification-content'>
                    <div className='notification-main'>
                        <div className='skeleton-username-bold' />
                        <div className='skeleton-follow-text' />
                        <div className='skeleton-timestamp' />
                    </div>
                    <div className='notification-action'>
                        <div className='skeleton-view-all-btn' />
                    </div>
                </div>
            </div>
        );
    }

    if (isNotificationsPage && notificationType === 'reply') {
        return (
            <div className='notification-skeleton-item reply-notification animate-pulse status-skeleton-item-notification'>
                <div className='reply-header'>
                    <div className='skeleton-reply-icon' />
                    <div className='skeleton-reply-text' />
                </div>
                <div className='reply-content'>
                    <div className='skeleton-avatar-medium' />
                    <div className='reply-main'>
                        <div className='reply-user-info'>
                            <div className='skeleton-username-medium' />
                            <div className='skeleton-timestamp-small' />
                        </div>
                        <div className='skeleton-reply-message' />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='status-skeleton-item animate-pulse'>
            <div className='skeleton-content'>
                <div className='skeleton-avatar' />

                <div className='skeleton-main'>
                    <div className='skeleton-header'>
                        <div className='skeleton-user-info'>
                            <div className='skeleton-username' />
                            {index % 2 === 0 && (
                                <div className='skeleton-follow-btn' />
                            )}
                        </div>
                        <div className='skeleton-menu'>
                            <div className='skeleton-dot' />
                            <div className='skeleton-dot' />
                            <div className='skeleton-dot' />
                        </div>
                    </div>

                    <div className='skeleton-timestamp'>
                        <div className='skeleton-clock-icon' />
                        <div className='skeleton-time' />
                    </div>

                    <div className='skeleton-content-text'>
                        {index % 2 === 0 ? (
                            <>
                                <div className='skeleton-line three-quarter' />
                                <div
                                    className='skeleton-line full-width'
                                    style={{
                                        height: `${lineHeight}px`,
                                    }}
                                />
                            </>
                        ) : (
                            <>
                                <div
                                    className='skeleton-line full-width'
                                    style={{
                                        height: `${lineHeight}px`,
                                    }}
                                />
                                <div className='skeleton-line three-quarter' />
                            </>
                        )}
                    </div>

                    <div className='skeleton-stats'>
                        <div className='skeleton-likes'>
                            <div className='skeleton-like-count' />
                        </div>
                        <div className='skeleton-engagement'>
                            <div className='skeleton-stat comment' />
                            <div className='skeleton-stat share' />
                            <div className='skeleton-stat views' />
                        </div>
                    </div>

                    <div className='skeleton-actions'>
                        <div className='skeleton-buttons'>
                            <div className='skeleton-action-btn'>
                                <div className='skeleton-icon' />
                                <div className='skeleton-label like' />
                            </div>
                            <div className='skeleton-action-btn'>
                                <div className='skeleton-icon' />
                                <div className='skeleton-label comment' />
                            </div>
                            <div className='skeleton-action-btn'>
                                <div className='skeleton-icon' />
                                <div className='skeleton-label share' />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const StatusSkeletonLoading: React.FC<StatusSkeletonLoadingProps> = ({ count = 5, disabled = false, isDetailStatus = false, isFollowPage = false, onClick = () => {} }) => {
    const isNotificationsPage = window.location.href.includes('notifications');

    const currentCount = isNotificationsPage ? 18 : count;

    const notificationTypes: ('favorite' | 'follow' | 'reply')[] = ['favorite', 'follow', 'reply'];

    const skeletonArray = isNotificationsPage
        ? Array.from({ length: currentCount }, () => {
            const randomIndex = Math.floor(Math.random() * notificationTypes.length);
            return notificationTypes[randomIndex];
        })
        : [];

    return (
        <div
            disabled={disabled}
            className='status-skeleton-loading'
            role='status'
            aria-busy='true'
            aria-live='polite'
            aria-label='Loading posts...'
            onClick={onClick}
        >
            {Array.from({ length: currentCount }, (_, index) => (
                <StatusSkeletonItem
                    key={index}
                    index={index}
                    count={currentCount}
                    isFollowPage={isFollowPage}
                    isDetailStatus={isDetailStatus}
                    isNotificationsPage={isNotificationsPage}
                    notificationType={isNotificationsPage ? skeletonArray[index] : undefined}
                />
            ))}
        </div>
    );
};
